import { useCallback, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@store/index';
import { setChatOpen, selectChatOpen } from '@features/ui/uiSlice';
import { setCurrentMode, setModeData, resetMode } from '@features/chat/chatModeSlice';
import { VerificationSession } from '../../../types/verification';
import { buildDrbfmTraceability, serializeSessionForChat } from './chatContext';

interface VerificationChat {
  active: boolean;
  toggle: () => void;
}

export function useVerificationChat(
  selectedSession: VerificationSession | null,
): VerificationChat {
  const dispatch = useAppDispatch();
  const chatOpen = useAppSelector(selectChatOpen);
  const [verificationChatActive, setVerificationChatActive] = useState(false);

  const handleToggleChat = useCallback(() => {
    if (!verificationChatActive) {
      // Open chat in verification mode
      dispatch(setCurrentMode('verification'));
      if (selectedSession) {
        dispatch(setModeData({
          verification_session: serializeSessionForChat(selectedSession),
          drbfm_traceability: buildDrbfmTraceability(selectedSession),
        }));
      }
      dispatch(setChatOpen(true));
      setVerificationChatActive(true);
    } else {
      // Close chat and reset mode
      dispatch(setChatOpen(false));
      dispatch(resetMode());
      setVerificationChatActive(false);
    }
  }, [dispatch, selectedSession, verificationChatActive]);

  // Sync session data to chat mode when selected session changes
  useEffect(() => {
    if (verificationChatActive && selectedSession) {
      dispatch(setModeData({
          verification_session: serializeSessionForChat(selectedSession),
          drbfm_traceability: buildDrbfmTraceability(selectedSession),
        }));
    }
  }, [dispatch, selectedSession, verificationChatActive]);

  // Reset mode on unmount
  useEffect(() => {
    return () => {
      if (verificationChatActive) {
        dispatch(resetMode());
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verificationChatActive]);

  // Track external chat close
  useEffect(() => {
    if (verificationChatActive && !chatOpen) {
      dispatch(resetMode());
      setVerificationChatActive(false);
    }
  }, [chatOpen, verificationChatActive, dispatch]);

  return { active: verificationChatActive, toggle: handleToggleChat };
}
